/** @jsxImportSource @emotion/react */
import { accordionHeader, accordionHeaderParent } from '../HazardControls.styles'
import { HazardDetail } from '@app/components/Molecules/MapView/RiskMapView/AddLayersDialog'
import { CombinedHazardDetail, getScenarioByHazardDetail } from '../HazardControls'
import { useSelector } from 'react-redux'
import { RootState } from '@app/redux/store'
import { usePreferences } from '@app/hooks/usePreferences'
import { LayerControlButtons } from '../../LayersOrganizer/LayerControlButtons'
import { useLayerListDetailsFetcher } from '@app/components/Molecules/RiskSideDrawerContent/data_fetchers/layerDetailsFetcher'
import { useCallback, useMemo } from 'react'
import { MapLayer } from '@app/redux/riskMap/riskMapSlice'
import { ModalLayer } from '@app/components/Molecules/LayersControl'
import { useLayers } from '@app/contexts/LayerContext'
import { Box } from '@mui/material'

export interface HazardControlFormHeaderProps {
  hazardDetails: HazardDetail | CombinedHazardDetail
}

export const HazardControlFormHeader = ({ hazardDetails }: HazardControlFormHeaderProps) => {
  const { layers: storeLayers } = useSelector((state: RootState) => state.riskMap)
  const mapLayer = getScenarioByHazardDetail(storeLayers, hazardDetails as CombinedHazardDetail)
  const { use_hazard_name } = usePreferences()

  // Additions
  const { prepareAndUpdateLayers } = useLayers()
  const layers = useMemo(() => {
    return storeLayers.map((layer) => {
      return { ...layer, id: layer.id ?? layer.tilesets.map((tileset) => tileset.id).join('-') }
    })
  }, [storeLayers])

  const modalLayers = storeLayers.reduce<ModalLayer[]>((acc, layer) => {
    const layerType = layer.layerType
    const layerGroup = acc.find((group) => group.id === layerType)

    if (layerGroup) {
      layerGroup.layers.push(layer)
    } else {
      acc.push({
        id: layerType,
        layers: [layer],
      })
    }

    return acc
  }, [])

  const { layerDetailsList } = useLayerListDetailsFetcher(layers)

  const layerDetails =
    layerDetailsList && mapLayer
      ? (layerDetailsList
          .flatMap((group) => (Array.isArray(group) ? group : []))
          .find(
            (report) =>
              Array.isArray(report) &&
              report.some(
                (detail) =>
                  detail &&
                  (detail.layer_id === mapLayer.type ||
                    detail.layer_id === mapLayer.assetTag ||
                    detail.layer_id === mapLayer.id.replace('urbanintelligence.', '')),
              ),
          ) ?? null)
      : null

  const onUpdateLayersOrder = (newLayers: ModalLayer[]) => {
    const updatedFlatLayers = newLayers.map((layer) => layer.layers).flat()
    // if layers or updatedFlatLayers aren't empty, update the layers
    if (
      storeLayers.length ||
      updatedFlatLayers.length ||
      newLayers.length != updatedFlatLayers.length
    ) {
      prepareAndUpdateLayers({ layers: updatedFlatLayers })
    }
  }
  const handleDeleteLayer = useCallback(
    ({ selectedLayer }: { selectedLayer: MapLayer }) => {
      const updatedLayers = modalLayers.map((layer) => {
        if (layer.id === selectedLayer.layerType) {
          return {
            ...layer,
            layers: layer.layers.filter(
              (layer) => layer.display_name !== selectedLayer.display_name,
            ),
          }
        }
        return layer
      })

      onUpdateLayersOrder(updatedLayers)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layers],
  )

  return (
    <div css={accordionHeaderParent}>
      <div css={accordionHeader}>
        <Box
          sx={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            flexShrink: '1',
          }}
        >
          {use_hazard_name.flag
            ? (mapLayer?.hazard_name ?? mapLayer?.display_name ?? hazardDetails.title)
            : mapLayer?.display_name || hazardDetails.title}
        </Box>
        {mapLayer && (
          <LayerControlButtons
            layer={mapLayer}
            layerDetails={layerDetails}
            isDialogPreview={false}
            handleDeleteLayer={handleDeleteLayer}
          />
        )}
      </div>
    </div>
  )
}
